import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/macro';

import { Upload } from '@oca/icons';
import { ImageCropDialog } from '@oca/ui';

import { useFileUploading } from '../hooks/use-file-uploading';
import { signUrl } from '../lib/sign-url';

export function AvatarUploader({ src, size, onUploaded, onCropDialogToggle }) {
  const { result, onUpload } = useFileUploading();
  const [isOpen, setIsOpen] = React.useState(false);

  function handleRemove() {
    if (onUploaded) onUploaded(null);
  }

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    setIsOpen(true);

    if (onCropDialogToggle) {
      onCropDialogToggle(true);
    }
  }

  React.useEffect(() => {
    if (result.length > 0) {
      const data = result[0];
      if (onUploaded) onUploaded(data);
    }
  }, [onUploaded, result]);

  function handleCropSave({ url, filename }) {
    setIsOpen(false);

    if (onCropDialogToggle) {
      onCropDialogToggle(false);
    }

    fetch(url)
      .then(res => res.blob())
      .then(blob => {
        const image = blob;
        image.lastModifiedDate = Date.now();
        image.name = filename;
        onUpload([image]);
      });
  }

  function handleCloseCrop() {
    setIsOpen(false);

    if (onCropDialogToggle) {
      onCropDialogToggle(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();

    // eslint-disable-next-line prefer-destructuring
    const files = e.dataTransfer.files;

    if (files && files.length > 0) {
      onUpload(files);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  return (
    <>
      <Dropzone
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <IconWrapper>
          <Upload size={28} />
        </IconWrapper>

        <TextPrimary>
          <span className="click">
            <Trans>Click to upload</Trans>
          </span>{' '}
          <Trans>or drag and drop</Trans>
        </TextPrimary>

        <TextSecondary>
          <Trans>SVG, PNG, JPG or GIF (max. 800×400px)</Trans>
        </TextSecondary>
      </Dropzone>

      {isOpen && (
        <ImageCropDialog
          title={<Trans>Adjust image</Trans>}
          src={src ? signUrl(src) : ''}
          onClose={handleCloseCrop}
          onCrop={handleCropSave}
          onRemove={handleRemove}
        />
      )}
    </>
  );
}

AvatarUploader.propTypes = {
  src: PropTypes.string,
  onUploaded: PropTypes.func,
  onCropDialogToggle: PropTypes.func,
  size: PropTypes.number,
};

AvatarUploader.defaultProps = {
  src: undefined,
  onUploaded: undefined,
  onCropDialogToggle: undefined,
  size: undefined,
};

const Dropzone = styled.div`
  width: 100%;
  padding: 28px 16px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: 0.2s ease;
  box-sizing: border-box;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextPrimary = styled.div`
  font-size: 14px;
  color: #4b5563;

  .click {
    color: #7c3aed;
    font-weight: 500;
  }
`;

const TextSecondary = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
`;
